"use client";

import { Button, Input, Label } from "@pandawa-qfast-core/react-ui-kit";
import { AlertCircleIcon, CheckCircleIcon, EyeIcon, EyeOffIcon, LoaderIcon, LockIcon } from "lucide-react";
import Link from "next/link";
import { useActionState } from "react";

import { resetPasswordAction, ResetPasswordActionState } from "@entities/auth";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

type Props = {
  token?: string;
};

const resetPasswordActionInitialState: ResetPasswordActionState = {
  success: false,
};

export function ResetPasswordForm({ token }: Props) {
  const [state, formAction, pending] = useActionState(resetPasswordAction, resetPasswordActionInitialState);

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const invalidToken = !token;

  return (
    <div>
      <Link
        href="/"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Kembali ke login
      </Link>

      {state.success ? (
        <div className="space-y-5 fade-in-top">
          <div className="bg-green-50 border border-green-200 text-green-700 px-5 py-6 rounded-xl text-center">
            <CheckCircleIcon className="h-12 w-12 mx-auto mb-3 text-green-500" />
            <h3 className="font-semibold text-lg mb-2">Password Berhasil Diubah!</h3>
            <p className="text-sm">Password Anda telah berhasil direset.</p>
          </div>

          <Button asChild className="w-full h-11 font-bold bg-primary hover:bg-primary-600">
            <Link href="/">Login Sekarang</Link>
          </Button>
        </div>
      ) : (
        <form action={formAction} className="space-y-5">
          <input type="hidden" name="token" value={token || ""} />

          {(state.message || invalidToken) && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2.5 fade-in-top">
              <AlertCircleIcon className="h-5 w-5 mt-0.5 flex-shrink-0 text-red-400" />
              <span>{invalidToken ? "Token reset password tidak valid atau sudah kadaluarsa." : state.message}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-foreground">
              Password Baru
            </Label>
            <div className="relative">
              <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Masukkan password baru"
                disabled={pending || invalidToken}
                defaultValue={state.fields?.password}
                className="pl-10 pr-10 h-11"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                tabIndex={-1}
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOffIcon className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <EyeIcon className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            {state.errors?.password?.[0] && <p className="text-xs text-red-500 mt-1">{state.errors.password[0]}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password_confirmation" className="text-sm font-medium text-foreground">
              Konfirmasi Password
            </Label>
            <div className="relative">
              <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                id="password_confirmation"
                name="password_confirmation"
                type={showPasswordConfirmation ? "text" : "password"}
                placeholder="Konfirmasi password baru"
                disabled={pending || invalidToken}
                defaultValue={state.fields?.password_confirmation}
                className="pl-10 pr-10 h-11"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                tabIndex={-1}
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
              >
                {showPasswordConfirmation ? (
                  <EyeOffIcon className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <EyeIcon className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            {state.errors?.password_confirmation?.[0] && (
              <p className="text-xs text-red-500 mt-1">{state.errors.password_confirmation[0]}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full h-11 font-bold text-sm bg-primary hover:bg-primary-600 transition-colors shadow-sm"
            disabled={pending || invalidToken}
          >
            {pending ? (
              <>
                <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                Memproses...
              </>
            ) : (
              "Reset Password"
            )}
          </Button>
        </form>
      )}
    </div>
  );
}
